// Parameters (Operator): the ontology of grid-code parameters with synonyms, replacements, requirements, markets and capabilities (S5); typed search params drive every filter.
import { createFileRoute, stripSearchParams } from "@tanstack/react-router";
import { useCallback } from "react";
import { z } from "zod";

import { zParameterStatus } from "@/api/__generated__/zod.gen";
import { optionalString, enumWithDefault, booleanWithDefault, listWithDefault } from "@/app/search";
import { RestrictedRoute } from "@/app/shell/RouteStates";
import { ParametersScreen } from "@/features/vocabulary/parameters/ParametersScreen";
import type { ParametersSearchPatch } from "@/features/vocabulary/parameters/search";

const searchSchema = z.object({
  group: optionalString(),
  status: listWithDefault(zParameterStatus, ["approved"]),
  q: optionalString(),
  semantic: booleanWithDefault(false),
  param: optionalString(),
  sort: enumWithDefault(["requirements", "id", "name"], "requirements"),
  dir: enumWithDefault(["asc", "desc"], "desc"),
});

const searchDefaults = {
  status: ["approved"],
  semantic: false,
  sort: "requirements",
  dir: "desc",
} satisfies Partial<z.output<typeof searchSchema>>;

export const Route = createFileRoute("/_app/$region/parameters")({
  validateSearch: searchSchema,
  search: { middlewares: [stripSearchParams(searchDefaults)] },
  staticData: { crumb: "Parameters" },
  component: ParametersRoute,
});

function ParametersRoute() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const update = useCallback(
    (patch: ParametersSearchPatch) => {
      void navigate({ search: (previous) => ({ ...previous, ...patch }), replace: true });
    },
    [navigate],
  );
  return (
    <RestrictedRoute level="O" description="The parameter ontology is for operators.">
      <ParametersScreen search={search} onSearchChange={update} />
    </RestrictedRoute>
  );
}
